import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as PenIcon } from 'assets/icons/pen.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/tresh.svg';
import clsx from 'clsx';
import IconButton from 'components/IconButton/IconButton';
import Table from 'components/Table/Table';
import TableBody from 'components/Table/TableBody';
import TableCell from 'components/Table/TableCell';
import TableHead from 'components/Table/TableHead';
import TableRow from 'components/Table/TableRow';
import { OPTION_ROLE_EMPLOYEES } from 'constants/general';

import type ISelectSupplierTable from './interfaces/IItemsTable';

import styles from './scss/ItemsTable.module.scss';

function ItemsTable({
  items,
  headCells,
  onEditRecord,
  onLoadMore,
  hasMore,
  isLoadingMore,
  isInfoTable,
  onDeleteItem,
  employeesEnumList,
  isViewOnly = false,
}: ISelectSupplierTable) {
  const { t } = useTranslation();
  const hyphen = t('common.hyphen');

  const filteredHeadCells = headCells.filter(({ id }) => {
    if (id === 'actions' || id === 'action') {
      return !isViewOnly;
    }
    return true;
  });

  const scrollRef = useRef<HTMLDivElement>(null);
  const throttleTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const el = scrollRef.current;
    const handleScroll = () => {
      if (throttleTimeout.current) return;
      throttleTimeout.current = setTimeout(() => {
        if (!el || isLoadingMore || !hasMore) {
          throttleTimeout.current = null;
          return;
        }
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 50) {
          onLoadMore();
        }
        throttleTimeout.current = null;
      }, 150);
    };
    if (el) {
      el.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (el) {
        el.removeEventListener('scroll', handleScroll);
      }
      if (throttleTimeout.current) {
        clearTimeout(throttleTimeout.current);
        throttleTimeout.current = null;
      }
    };
  }, [isLoadingMore, hasMore, onLoadMore]);

  return (
    <div className={styles.root}>
      <div
        className={styles.tableBodyScroll}
        ref={scrollRef}
        aria-label="Employee table body"
        role="region"
      >
        <Table
          className={clsx(styles.table, {
            [styles.tableInfoTable]: isInfoTable,
            [styles.tableViewOnly]: isViewOnly,
          })}
        >
          <TableHead className={clsx(styles.header, {
            [styles.headerInfoTable]: isInfoTable,
          })}
          >
            {filteredHeadCells.map(({
              id, label,
            }) => (
              <TableCell head className={styles.tableCell} key={id}>
                <div>
                  <span className={styles.tableCellLabel}>
                    {t(label)}
                  </span>
                </div>
              </TableCell>
            ))}
          </TableHead>
          <TableBody className={styles.body}>
            {items ? items?.map((item, index) => (
              <TableRow key={[item.id, index].join('_')}>
                <TableCell>
                  <div className={styles.staticCell}>
                    <span>
                      {employeesEnumList.find((option) => option.value === item.eployeeDepartment)?.label
                     || hyphen}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  {OPTION_ROLE_EMPLOYEES.find((option) => option.value === item.eployeeRole)?.label || hyphen}
                </TableCell>
                <TableCell>
                  {item.contactName || hyphen}
                </TableCell>
                <TableCell>
                  {item.contactEmail || hyphen}
                </TableCell>
                {!isViewOnly && (
                  <TableCell>
                    <IconButton
                      icon={<PenIcon />}
                      onClick={() => onEditRecord(item.id || -1)}
                    />
                    <IconButton
                      icon={<TrashIcon />}
                      onClick={() => onDeleteItem?.(item.id || -1)}
                    />
                  </TableCell>
                )}
              </TableRow>
            )) : null}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default ItemsTable;
